/**
 * Activity / Audit filter vocabulary + small pure helpers (Story 34.5).
 *
 * The Action + Resource-type pick lists are derived from the controlled
 * `@provenleap/audit` ACTIONS vocabulary (single source of truth) so the filter
 * toolbar never drifts from what the writer can actually emit.
 */

import { ACTIONS } from "@provenleap/audit";

export interface ActionOption {
  /** The wire value, e.g. "report.created". */
  value: string;
  /** A human label for the dropdown, e.g. "Report created". */
  label: string;
  /** The owning resource (for grouping), e.g. "report". */
  resourceType: string;
}

function prettifyAction(action: string): string {
  // "report.created" → "Report created"; "team_member.role_changed" →
  // "Team member role changed".
  const words = action.replace(/[._]/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function prettifyResource(resourceType: string): string {
  const words = resourceType.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** All actions in the vocabulary, sorted for a stable picker order. */
export const ACTION_OPTIONS: ActionOption[] = Object.values(ACTIONS)
  .map((a) => ({
    value: a.action,
    label: prettifyAction(a.action),
    resourceType: a.resourceType,
  }))
  .sort((x, y) => x.value.localeCompare(y.value));

/** Distinct resource types, sorted, with display labels. */
export const RESOURCE_TYPE_OPTIONS: Array<{ value: string; label: string }> =
  Array.from(new Set(Object.values(ACTIONS).map((a) => a.resourceType)))
    .sort()
    .map((rt) => ({ value: rt, label: prettifyResource(rt) }));

// ---------------------------------------------------------------------------
// Bold-markdown inline renderer
// ---------------------------------------------------------------------------

export type BoldSegment = { text: string; bold: boolean };

/**
 * Split a `humanizeAction` sentence on `**…**` markers into segments. A tiny,
 * dependency-free inline renderer — the caller wraps `bold` segments in
 * `<strong>`. Unbalanced markers are treated as literal text.
 */
export function parseBoldMarkdown(input: string): BoldSegment[] {
  const segments: BoldSegment[] = [];
  // Match **…** lazily; everything else is plain text.
  const re = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(input)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: input.slice(lastIndex, match.index), bold: false });
    }
    segments.push({ text: match[1]!, bold: true });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < input.length) {
    segments.push({ text: input.slice(lastIndex), bold: false });
  }
  return segments;
}

// ---------------------------------------------------------------------------
// Diff comparison
// ---------------------------------------------------------------------------

export type DiffChangeKind = "added" | "removed" | "modified" | "unchanged";

export interface DiffRow {
  key: string;
  before: unknown;
  after: unknown;
  kind: DiffChangeKind;
}

function stableStringify(value: unknown): string {
  if (value === undefined) return " undefined";
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Build a side-by-side diff row list from before/after objects. A key present
 * only in `after` is "added", only in `before` is "removed", in both but
 * different is "modified", otherwise "unchanged".
 */
export function buildDiffRows(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined
): DiffRow[] {
  const b = before ?? {};
  const a = after ?? {};
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)])).sort();
  return keys.map((key) => {
    const inBefore = Object.prototype.hasOwnProperty.call(b, key);
    const inAfter = Object.prototype.hasOwnProperty.call(a, key);
    let kind: DiffChangeKind;
    if (inBefore && !inAfter) kind = "removed";
    else if (!inBefore && inAfter) kind = "added";
    else if (stableStringify(b[key]) !== stableStringify(a[key])) kind = "modified";
    else kind = "unchanged";
    return { key, before: b[key], after: a[key], kind };
  });
}

/** Render a diff value for display (objects → compact JSON, primitives → string). */
export function formatDiffValue(value: unknown): string {
  if (value === undefined) return "—";
  if (value === null) return "null";
  if (typeof value === "object") return stableStringify(value);
  return String(value);
}
